const PER_PAGE = 10;

export const state = () => ({
  isOpen: false,
  extraBottomSpace: false,
  hasPreviousMessages: true,
  messages: [],
  threads: [],
  currentThreadId: undefined,
});

export const getters = {
  currentPage: state => Math.max(1, Math.floor(state.messages.length / PER_PAGE)),
};

export const mutations = {
  assign(state, payload) {
    Object.assign(state, payload);
  },
  toggle(state) {
    state.isOpen = !state.isOpen;
  },
  setExtraBottomSpace(state, value) {
    state.extraBottomSpace = value;
  },
  addMessage(state, message) {
    state.messages = [...state.messages, message];
  },
  setMessages(state, messages) {
    state.messages = messages;
  },
};

export const actions = {
  async getThreads({ commit }) {
    const { $apiClient } = useNuxtApp();
    const response = await $apiClient.ai.chat.threads.list();

    commit('assign', {
      threads: response,
    });

    if (response.length === 0) {
      commit('assign', {
        currentThreadId: undefined,
        messages: [],
        hasPreviousMessages: false,
      });
    }
  },
  async deleteThread({ state, commit, dispatch }, threadId: string) {
    const { $apiClient } = useNuxtApp();

    await $apiClient.ai.chat.threads.delete(threadId);

    commit('assign', {
      threads: state.threads.filter(thread => thread.id !== threadId),
    });

    if (state.currentThreadId === threadId) {
      if (state.threads.length > 0) {
        await dispatch('setCurrentThread', state.threads[0].id);
      } else {
        commit('assign', {
          currentThreadId: undefined,
          messages: [],
          hasPreviousMessages: false,
        });
      }
    }
  },
  async setCurrentThread({ commit, dispatch }, threadId: string) {
    commit('assign', {
      currentThreadId: threadId,
      messages: [],
      hasPreviousMessages: false,
    });

    if (threadId) await dispatch('getMessages');
  },
  async createThread({ state, commit, dispatch }) {
    const { $apiClient } = useNuxtApp();
    const response = await $apiClient.ai.chat.threads.create();

    commit('assign', {
      threads: [...state.threads, response],
    });

    await dispatch('setCurrentThread', response.id);
  },
  async getPreviousMessages({ state, commit, getters }) {
    const { $apiClient } = useNuxtApp();
    const nextPage = getters.currentPage + 1;

    const response = await $apiClient.ai.chat.messages.list({
      page: nextPage,
      ai_chat_thread_id: state.currentThreadId,
    });

    // As the user can send more messages after get the previous ones, we need to check if the last message is already present in the current paginated list
    // Example: if the user gets 10 messages and sends and/or receives 2 more, we will have a page with only 2 messages
    // And when the user asks for the previous messages, the request will have the entire page, including the 2 messages that are already present in the current list
    // So we need to check if the older message in the current list is present in the requested page and remove the already present messages
    const alreadyPresentMessageIndex = response.findIndex(message => message.id === state.messages[0].id);
    const sliceSize = alreadyPresentMessageIndex === -1 ? response.length : alreadyPresentMessageIndex;
    const newMessages = response.slice(0, sliceSize);

    commit('assign', {
      messages: [...newMessages, ...state.messages],
      hasPreviousMessages: response.length === PER_PAGE,
    });
  },
  async getMessages({ state, commit }) {
    const { $apiClient } = useNuxtApp();
    const response = await $apiClient.ai.chat.messages.list({
      ai_chat_thread_id: state.currentThreadId,
    });

    commit('assign', {
      messages: response,
      hasPreviousMessages: response.length === PER_PAGE,
    });
  },
  async sendMessage({ state, commit }, content: string) {
    const { $apiClient } = useNuxtApp();

    commit('addMessage', {
      content,
      role: 'user',
    });

    try {
      const response = await $apiClient.ai.chat.messages.create({
        content,
        ai_chat_thread_id: state.currentThreadId,
      });

      // the api will return an array of messages, including the one we just sent
      commit('setMessages', [
        ...state.messages.slice(0, state.messages.length - 1),
        ...response,
      ]);
    } catch (error) {
      // revert last message
      commit('setMessages', state.messages.slice(0, state.messages.length - 1));
      throw error;
    }
  },
  async updateMessageUseful({ state, commit }, { id, useful }) {
    const { $apiClient } = useNuxtApp();

    const previousUseful = state.messages.find(message => message.id === id).useful;

    commit('setMessages', state.messages.map((message) => {
      if (message.id === id) {
        return {
          ...message,
          useful,
        };
      }

      return message;
    }));

    try {
      await $apiClient.ai.chat.messages.update(id, { useful });
    } catch (error) {
      // revert useful status
      commit('setMessages', state.messages.map((message) => {
        if (message.id === id) {
          return {
            ...message,
            useful: previousUseful,
          };
        }

        return message;
      }));

      throw error;
    }
  },
};

export const namespaced = true;
